import React, { useCallback, useContext, useReducer } from 'react';
import Strategies from '../../pages/SpoolApp/CreateASpool/Strategies';
import RiskModel from '../../pages/SpoolApp/CreateASpool/RiskModel';
import RiskTolerance from '../../pages/SpoolApp/CreateASpool/RiskTolerance';
import SpoolAsset from '../../pages/SpoolApp/CreateASpool/SpoolAsset';
import CreateSpool from '../../pages/SpoolApp/CreateASpool/CreateSpool';
import CreateASpoolContext from './createASpoolContext';
import createASpoolReducer from './CreateASpoolReducer';
import modalContext from '../modal/modalContext';
import { CREATE_SPOOL_PENDING, SPOOL_CREATED } from '../../config/constants';
import appContext from '../app/appContext';
import { SpoolCreationInputDto } from '../types/spool-local';
import errorContext from '../error/errorContext';
import { useHistory } from 'react-router-dom';
import { sleep } from '../../web3/utils';
import useSpoolsCreatedByUser from '../../hooks/web3/app/useSpoolsCreatedByUser';
import isProfane from '../../helpers/isProfane';

const CreateASpoolState: React.FC = ({ children }) => {
    const { sdk } = useContext(appContext);
    const { openModal, closeModal, setModalData } = useContext(modalContext);

    const { setError } = useContext(errorContext);

    const { spoolsCreatedByUser, updateOnCreate } = useSpoolsCreatedByUser();

    const history = useHistory();

    const setCurrentStep = useCallback(
        (step: number): void =>
            dispatch({
                type: 'SET_CURRENT_STEP',
                payload: step,
            }),
        []
    );

    const setStepCompletedStatus = useCallback(
        (stepNumber: number, status: boolean): void =>
            dispatch({
                type: 'SET_STEP_COMPLETED',
                payload: { stepNumber, status },
            }),
        []
    );

    const setSelectedAsset = useCallback(
        (asset: string | null): void =>
            dispatch({
                type: 'SET_SELECTED_ASSET',
                payload: asset,
            }),
        []
    );

    const setSelectedRiskModel = useCallback(
        (riskModel: string | null): void =>
            dispatch({
                type: 'SET_SELECTED_RISK_MODEL',
                payload: riskModel,
            }),
        []
    );

    const addStrategy = useCallback(
        (strategy: string): void =>
            dispatch({
                type: 'ADD_STRATEGY',
                payload: strategy,
            }),
        []
    );

    const removeStrategy = useCallback(
        (strategy: string): void =>
            dispatch({
                type: 'REMOVE_STRATEGY',
                payload: strategy,
            }),
        []
    );

    const setRiskTolerance = useCallback(
        (riskTolerance: number): void =>
            dispatch({
                type: 'SET_RISK_TOLERANCE',
                payload: riskTolerance,
            }),
        []
    );

    const setAllocations = useCallback((allocations: Record<string, string>): void => {
        dispatch({
            type: 'SET_ALLOCATIONS',
            payload: allocations,
        });
    }, []);

    const setBaseApy = useCallback((apy: number): void => {
        dispatch({
            type: 'SET_BASE_APY',
            payload: apy,
        });
    }, []);

    const setSpoolName = useCallback(
        (name: string): void =>
            dispatch({
                type: 'SET_SPOOL_NAME',
                payload: name,
            }),
        []
    );

    const setSpoolFee = useCallback(
        (fee: number): void =>
            dispatch({
                type: 'SET_SPOOL_FEE',
                payload: fee,
            }),
        []
    );

    const back = () => {
        if (state.currentStep < 2) return;

        setCurrentStep(state.currentStep - 1);
    };

    const next = () => {
        if (state.currentStep > Object.keys(state.steps).length - 1) return;
        setCurrentStep(state.currentStep + 1);
    };

    const createSpool = useCallback(
        async (params: SpoolCreationInputDto) => {
            if (!sdk) return;

            try {
                params.name = params.name.trim();

                setModalData((prev) => ({ ...prev, message: CREATE_SPOOL_PENDING }));
                openModal('waitingModal');

                const create = await sdk.spools.createSpool(params);

                const receipt = await create.wait();

                await sleep(3000);

                const spool = await updateOnCreate(spoolsCreatedByUser?.length);

                history.push(
                    spool ? `/your-creator-fees/${spool.address}` : '/your-creator-fees'
                );

                setModalData((prev) => ({
                    ...prev,
                    status: SPOOL_CREATED,
                    txHash: receipt.transactionHash,
                    message: '',
                }));
                openModal('successModal');
            } catch (error) {
                setError(error);
            } finally {
                closeModal('waitingModal');
            }
        },
        [
            sdk,
            history,
            spoolsCreatedByUser,
            setError,
            closeModal,
            openModal,
            setModalData,
            updateOnCreate,
        ]
    );

    const validateSpoolName = useCallback(
        (spoolName: string): boolean => {
            if (isProfane(spoolName)) {
                openModal('warningModal');
                setModalData((prev) => ({
                    ...prev,
                    message: 'Spool Name includes profanities',
                }));
                return false;
            }

            if (!spoolName.match('^([a-zA-Z0-9 ]+)$')) {
                openModal('warningModal');
                setModalData((prev) => ({
                    ...prev,
                    message: 'Only characters from A-Z 0-9 are allowed',
                }));
                return false;
            }

            if (spoolName.length > 32) {
                openModal('warningModal');
                setModalData((prev) => ({
                    ...prev,
                    message: 'Spool Name must be less than 32 characters',
                }));
                return false;
            }

            return true;
        },
        [openModal, setModalData]
    );

    const initialState = {
        steps: [
            {
                label: 'Spool Asset',
                render: <SpoolAsset />,
                completed: false,
            },
            {
                label: 'Risk Model',
                render: <RiskModel />,
                completed: false,
            },
            {
                label: 'Strategies',
                render: <Strategies />,
                completed: false,
            },
            {
                label: 'Risk Appetite',
                render: <RiskTolerance />,
                completed: false,
            },
            {
                label: 'Create Spool',
                render: <CreateSpool />,
                completed: false,
            },
        ],
        currentStep: 1,
        selectedAsset: null,
        selectedRiskModel: null,
        selectedStrategies: [],
        riskTolerance: 0,
        allocations: null,
        baseApy: 0,
        spoolName: '',
        spoolFee: 0,
        setCurrentStep,
        setStepCompletedStatus,
        setSelectedAsset,
        setSelectedRiskModel,
        setRiskTolerance,
        setAllocations,
        setBaseApy,
        setSpoolName,
        setSpoolFee,
        addStrategy,
        removeStrategy,
        back,
        next,
        createSpool,
        validateSpoolName,
    };

    const [state, dispatch] = useReducer(createASpoolReducer, initialState);

    return (
        <CreateASpoolContext.Provider
            value={{
                ...initialState,
                steps: state.steps,
                currentStep: state.currentStep,
                selectedAsset: state.selectedAsset,
                selectedRiskModel: state.selectedRiskModel,
                selectedStrategies: state.selectedStrategies,
                riskTolerance: state.riskTolerance,
                allocations: state.allocations,
                baseApy: state.baseApy,
                spoolName: state.spoolName,
                spoolFee: state.spoolFee,
            }}
        >
            {children}
        </CreateASpoolContext.Provider>
    );
};

export default CreateASpoolState;
